import { useCallback } from 'react';
import { useAppDispatch } from '../../../hooks';
import { toggleSubmenu, setFocusOnNavItem } from '../../../reducers/navItems';
import getCurrentlyVisibleNavItems from '../../../components/VerticalNav/utils/getCurrentlyVisibleNavItems';
import useNavItems from '../../../hooks/useNavItems';
import { VERTICAL_NAV_GROUP, EXPANDABLE_NAV_GROUP } from '../../../components/VerticalNav/constants';
import { NAV_ITEM_BOOKMARKS_GROUP_ID } from '../../../constants/Bookmarks';
const useVerticalNavKeyboardControls = ({
  isBookmarksMenu
} = {}) => {
  const navItems = useNavItems();
  const dispatch = useAppDispatch();
  const setFocusToNavItem = useCallback(id => {
    dispatch(setFocusOnNavItem({
      id
    }));
  }, [dispatch]);
  const setFocusToFirstMenuItem = useCallback(id => {
    if (isBookmarksMenu) {
      const bookmarksMenu = navItems.byId[NAV_ITEM_BOOKMARKS_GROUP_ID].children;
      if (bookmarksMenu.length) {
        const firstBookmark = bookmarksMenu[0].id;
        setFocusToNavItem(firstBookmark);
      }
      return;
    }
    const currentItem = navItems.byId[id];
    const currentMenu = id.startsWith(EXPANDABLE_NAV_GROUP) || currentItem.ancestors.length > 0 ? getCurrentlyVisibleNavItems(navItems, currentItem ? currentItem.ancestors[currentItem.ancestors.length - 1] : undefined) : navItems.primaryNav.navItems;
    const firstMenuItem = currentMenu[0];
    setFocusToNavItem(firstMenuItem.id);
  }, [isBookmarksMenu, navItems, setFocusToNavItem]);
  const setFocusToSubmenu = useCallback((id, shouldShiftFocusOnOpen) => {
    const currentItem = navItems.byId[id];
    if (currentItem.children.length < 1) return;
    let firstChild = currentItem.children[0];
    if (firstChild.id.startsWith(VERTICAL_NAV_GROUP) || firstChild.id.startsWith(EXPANDABLE_NAV_GROUP)) {
      firstChild = firstChild.children[0];
    }
    dispatch(toggleSubmenu({
      id
    }));
    if (shouldShiftFocusOnOpen) {
      setFocusToFirstMenuItem(firstChild.id);
    }
  }, [dispatch, navItems, setFocusToFirstMenuItem]);
  const setFocusToPrimaryParentMenu = useCallback((id, shouldShiftFocusOnUpdate) => {
    const currentItem = navItems.byId[id];
    if (isBookmarksMenu) {
      dispatch(toggleSubmenu({
        id: NAV_ITEM_BOOKMARKS_GROUP_ID
      }));
      setFocusToNavItem(NAV_ITEM_BOOKMARKS_GROUP_ID);
      return;
    }
    if (!currentItem && id.startsWith(EXPANDABLE_NAV_GROUP) && navItems.openMenus.primary) {
      dispatch(toggleSubmenu({
        id: navItems.openMenus.primary
      }));
      setFocusToNavItem(navItems.openMenus.primary);
      return;
    }
    if (currentItem.ancestors.length < 1) return;
    const lastAncestor = currentItem.ancestors[0];
    if (shouldShiftFocusOnUpdate) {
      dispatch(toggleSubmenu({
        id: lastAncestor
      }));
      setFocusToNavItem(lastAncestor);
      return;
    }
    dispatch(toggleSubmenu({
      id
    }));
  }, [dispatch, isBookmarksMenu, navItems.byId, navItems.openMenus.primary, setFocusToNavItem]);
  const setFocusToMenuItem = useCallback(({
    id,
    offset = 0
  }) => {
    const currentItem = navItems.byId[id];
    const currentMenu = id.startsWith(EXPANDABLE_NAV_GROUP) || currentItem.ancestors.length > 0 ? getCurrentlyVisibleNavItems(navItems) : navItems.primaryNav.navItems;
    const currentIndex = currentMenu.findIndex(navItem => navItem.id === id);
    if (currentIndex < 0 || currentIndex >= currentMenu.length) return;
    const nextIndex = currentIndex + offset;
    if (nextIndex >= 0 && nextIndex < currentMenu.length) {
      setFocusToNavItem(currentMenu[nextIndex].id);
    }
  }, [navItems, setFocusToNavItem]);
  const setFocusToLastMenuItem = useCallback(id => {
    if (isBookmarksMenu) {
      const bookmarksMenu = navItems.byId[NAV_ITEM_BOOKMARKS_GROUP_ID].children;
      if (bookmarksMenu.length) {
        const lastBookmark = bookmarksMenu[bookmarksMenu.length - 1].id;
        setFocusToNavItem(lastBookmark);
      }
      return;
    }
    const currentItem = navItems.byId[id];
    const currentMenu = id.startsWith(EXPANDABLE_NAV_GROUP) || currentItem.ancestors.length > 0 ? getCurrentlyVisibleNavItems(navItems) : navItems.primaryNav.navItems;
    const lastMenuItem = currentMenu[currentMenu.length - 1];
    setFocusToNavItem(lastMenuItem.id);
  }, [isBookmarksMenu, navItems, setFocusToNavItem]);
  const onKeyDown = useCallback(({
    id,
    hasSubmenu = false,
    hasParentMenu = false,
    shouldShiftFocusOnOpen = false
  }) => e => {
    let stopDefaults = true;
    switch (e.key) {
      case 'Esc':
      case 'Escape':
        setFocusToPrimaryParentMenu(id, true);
        break;
      case 'Up':
      case 'ArrowUp':
        setFocusToMenuItem({
          id,
          offset: -1
        });
        break;
      case 'Down':
      case 'ArrowDown':
        setFocusToMenuItem({
          id,
          offset: 1
        });
        break;
      case 'Left':
      case 'ArrowLeft':
        if (hasParentMenu) {
          setFocusToPrimaryParentMenu(id, shouldShiftFocusOnOpen);
          break;
        }
        setFocusToMenuItem({
          id,
          offset: -1
        });
        break;
      case 'Right':
      case 'ArrowRight':
        if (hasSubmenu) {
          setFocusToSubmenu(id, shouldShiftFocusOnOpen);
          break;
        }
        setFocusToMenuItem({
          id,
          offset: 1
        });
        break;
      case 'Home':
      case 'PageUp':
        setFocusToFirstMenuItem(id);
        break;
      case 'End':
      case 'PageDown':
        setFocusToLastMenuItem(id);
        break;
      case ' ':
      case 'Enter':
        setFocusToSubmenu(id, shouldShiftFocusOnOpen);
        stopDefaults = hasSubmenu;
        break;
      default:
        return;
    }
    if (stopDefaults) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, [setFocusToFirstMenuItem, setFocusToLastMenuItem, setFocusToMenuItem, setFocusToPrimaryParentMenu, setFocusToSubmenu]);
  return {
    onKeyDown
  };
};
export default useVerticalNavKeyboardControls;